import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Activity, Move, Zap, Target, Play, Flame, Snowflake, Sun, Moon, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const AREA_ICONS = { BODY: Activity, MOBILITY: Move, "ARM ACTIVATION": Zap, "PLYOCARE THROWS": Target };

function ytEmbedUrl(url) {
  // accepts: https://youtu.be/ID?t=6, https://youtube.com/shorts/ID, https://youtu.be/ID?si=...
  if (!url) return null;
  const m = url.match(/youtu\.?be(?:\.com)?\/(?:shorts\/)?([\w-]{6,})/);
  if (!m) return null;
  const id = m[1];
  const tMatch = url.match(/[?&]t=(\d+)/);
  return `https://www.youtube.com/embed/${id}${tMatch ? `?start=${tMatch[1]}` : ""}`;
}

function HybridChip({ h, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-xl px-5 py-4 text-left transition-all ${active ? "ring-2" : "ring-1 ring-white/10 hover:ring-white/30"}`}
      style={{ backgroundColor: active ? `${h.color}1f` : "var(--ink-2)", borderColor: h.color, ringColor: h.color }}
      data-testid={`hybrid-chip-${h.letter}`}
    >
      <div className="flex items-center gap-3">
        <div className="font-display font-black text-4xl leading-none" style={{ color: h.color }}>{h.letter}</div>
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40">Hybrid {h.letter}</div>
          <div className="text-sm text-white font-semibold leading-tight">{h.name}</div>
        </div>
      </div>
      <div className="text-xs text-white/50 mt-2">{h.when}</div>
    </button>
  );
}

export default function PreThrowingRoutine() {
  const [data, setData] = useState(null);
  const [activeLetter, setActiveLetter] = useState("A");

  useEffect(() => {
    api.get("/pre-throwing-routine").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="text-white/60 p-8">Loading…</div>;

  const active = data.hybrids.find((h) => h.letter === activeLetter);

  return (
    <div className="space-y-8" data-testid="pre-throwing-routine">
      <div>
        <div className="text-[10px] tracking-[0.3em] font-bold text-white/40 uppercase">Module 2 · Pre-Throwing Routine</div>
        <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter mt-1 text-white">Warm-Up & Hybrids</h1>
        <p className="text-sm text-white/50 mt-2">Sourced from the SUNO Knights pitcher protocol. The 4-area warmup is the same across every hybrid — what changes is the throwing volume after.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {data.hybrids.map((h) => (
          <HybridChip key={h.letter} h={h} active={h.letter === activeLetter} onClick={() => setActiveLetter(h.letter)} />
        ))}
      </div>

      {active && (
        <div className="rounded-xl ink-card p-6" data-testid={`hybrid-detail-${active.letter}`}>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <div className="font-display font-black text-6xl leading-none" style={{ color: active.color }}>{active.letter}</div>
              <div>
                <h2 className="font-display font-bold text-2xl text-white">{active.name}</h2>
                <p className="text-sm text-white/60 mt-1">{active.when}</p>
              </div>
            </div>
          </div>
          <div className="mt-5">
            <div className="text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase mb-2">Throwing Volume</div>
            <ul className="space-y-1">
              {active.throwing_volume.map((line, i) => (
                <li key={i} className="text-sm text-white/85 font-mono-num flex items-baseline gap-2">
                  <span style={{ color: active.color }}>▸</span>{line}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        {data.warmup_blueprint.map((area) => {
          const Icon = AREA_ICONS[area.area] || Activity;
          return (
            <section key={area.area} className="rounded-xl ink-card overflow-hidden" data-testid={`warmup-area-${area.area.toLowerCase().replace(/\s+/g, "-")}`}>
              <header className="px-5 py-4 border-b border-[var(--ink-border)] flex items-center gap-3">
                <Icon className="h-5 w-5" style={{ color: "var(--brand-secondary)" }} />
                <h3 className="font-display font-bold text-lg text-white">{area.area}</h3>
                <span className="ml-auto text-[10px] tracking-[0.25em] font-bold text-white/40 uppercase">
                  {area.blocks.length} block{area.blocks.length !== 1 ? "s" : ""}
                </span>
              </header>
              <div className="divide-y divide-[var(--ink-border)]">
                {area.blocks.map((b, i) => {
                  const embed = ytEmbedUrl(b.video);
                  return (
                    <div key={i} className="px-5 py-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="font-semibold text-white">{b.label}</div>
                        {b.video && (
                          <a href={b.video} target="_blank" rel="noreferrer" className="text-xs text-[var(--brand-secondary)] hover:underline flex items-center gap-1 shrink-0" data-testid={`video-link-${i}`}>
                            <Play className="h-3 w-3" /> Video <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </div>
                      <ul className="mt-2 space-y-1">
                        {b.drills.map((d, j) => (
                          <li key={j} className="text-sm text-white/70 pl-4 relative before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:rounded-full" style={{}}>
                            <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full" style={{ backgroundColor: "var(--brand-secondary)", opacity: 0.5 }} />
                            {d}
                          </li>
                        ))}
                      </ul>
                      {embed && (
                        <div className="mt-3 rounded-lg overflow-hidden ring-1 ring-white/10 aspect-video bg-black">
                          <iframe
                            src={embed}
                            title={b.label}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="w-full h-full"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>

      <div className="text-xs text-white/30 text-center pt-4 italic">Source: {data.source}</div>
    </div>
  );
}
